import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Lock, Mail, User } from 'lucide-react';
import { OrbisLogo } from '@/components/OrbisLogo';
import OrbisSpinner from '@/components/ui/orbis-spinner';
import EtherealRingsSpline from '@/components/EtherealRingsSpline';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908C16.658 14.013 17.64 11.705 17.64 9.2z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
    <path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

const ArrowIcon = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
    <path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) { toast.error('Please fill in all fields'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setIsLoading(true);
    try {
      await register(name, email, password);
      toast.success('Account created!');
      navigate('/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden', background: '#08090E', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>

      {/* Spline — left 2/3 */}
      <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: '67vw', zIndex: 0 }}>
        <EtherealRingsSpline brightness={0.6} scale={1.0} />
      </div>

      {/* Vignette */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none',
        background: 'linear-gradient(to right, transparent 0%, transparent 46%, rgba(8,9,14,0.55) 58%, rgba(8,9,14,0.92) 65%, #08090E 72%)',
      }} />
      <div style={{
        position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none',
        background: 'linear-gradient(180deg, rgba(8,9,14,0.5) 0%, transparent 15%, transparent 85%, rgba(8,9,14,0.5) 100%)',
      }} />

      {/* Logo */}
      <div style={{ position: 'fixed', top: 36, left: 40, zIndex: 20 }}>
        <OrbisLogo size={22} variant="full" />
      </div>

      {/* Form panel — right 1/3 */}
      <div style={{
        position: 'relative', zIndex: 10,
        width: '33vw', minWidth: 340, maxWidth: 420,
        paddingRight: 'clamp(32px, 4vw, 64px)',
        paddingLeft: 24,
        display: 'flex', flexDirection: 'column',
      }}>

        <div style={{ marginBottom: 40 }}>
          <h1 style={{
            fontFamily: 'var(--orbis-font-display, Sora, sans-serif)',
            fontSize: 'clamp(42px, 5.2vw, 54px)',
            fontWeight: 200, color: 'var(--orbis-warm)',
            letterSpacing: '-0.04em', lineHeight: 1.0, marginBottom: 10,
          }}>
            Create account.
          </h1>
          <p style={{ fontSize: 14, fontWeight: 300, color: 'var(--orbis-t4)', letterSpacing: '0.01em' }}>
            Your AI web studio awaits
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          {/* Name */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontFamily: 'var(--orbis-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t4)' }}>Name</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px', background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.09)', borderRadius: 10, height: 50 }}>
              <User size={15} style={{ color: 'var(--orbis-t4)', flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input"
                style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: 14, fontWeight: 300, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)' }}
              />
            </div>
          </div>

          {/* Email */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontFamily: 'var(--orbis-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t4)' }}>Email</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px', background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.09)', borderRadius: 10, height: 50 }}>
              <Mail size={15} style={{ color: 'var(--orbis-t4)', flexShrink: 0 }} />
              <input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email-input"
                style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: 14, fontWeight: 300, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)' }}
              />
            </div>
          </div>

          {/* Password */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontFamily: 'var(--orbis-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t4)' }}>Password</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px', background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.09)', borderRadius: 10, height: 50 }}>
              <Lock size={15} style={{ color: 'var(--orbis-t4)', flexShrink: 0 }} />
              <input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password-input"
                style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: 14, fontWeight: 300, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)' }}
              />
            </div>
            <p style={{ fontSize: 11, fontWeight: 300, color: 'var(--orbis-t4)' }}>At least 6 characters</p>
          </div>

          {/* CTA — green for register */}
          <button
            type="submit"
            disabled={isLoading}
            data-testid="register-submit-btn"
            style={{
              marginTop: 6, width: '100%', height: 52, borderRadius: 100, border: 'none',
              background: 'var(--orbis-green)', color: '#08090E',
              fontSize: 15, fontWeight: 400, fontFamily: 'var(--orbis-font)',
              letterSpacing: '-0.01em', cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              boxShadow: '0 4px 28px rgba(143,196,160,0.18)',
              transition: 'opacity 0.15s, transform 0.12s',
            }}
          >
            {isLoading ? (
              <><OrbisSpinner size={16} color="#08090E" />Creating account...</>
            ) : (
              <>
                <span>Create account</span>
                <span style={{ width: 24, height: 24, borderRadius: '50%', background: 'rgba(8,9,14,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ArrowIcon />
                </span>
              </>
            )}
          </button>
        </form>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1, height: '0.5px', background: 'rgba(255,255,255,0.07)' }} />
          <span style={{ fontSize: 12, color: 'var(--orbis-t4)' }}>or</span>
          <div style={{ flex: 1, height: '0.5px', background: 'rgba(255,255,255,0.07)' }} />
        </div>

        {/* Google */}
        <button
          type="button"
          onClick={() => { window.location.href = `${API}/api/auth/google`; }}
          style={{
            width: '100%', height: 50, borderRadius: 100,
            border: '0.5px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)',
            color: 'var(--orbis-t2)', fontSize: 14, fontWeight: 300, fontFamily: 'var(--orbis-font)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            transition: 'background 0.15s, color 0.15s',
          }}
        >
          <GoogleIcon />
          Continue with Google
        </button>

        <div style={{ marginTop: 20 }}>
          <span style={{ fontSize: 13, fontWeight: 300, color: 'var(--orbis-t4)' }}>
            Already have an account?{' '}
            <Link to="/login" data-testid="login-link" style={{ color: 'var(--orbis-t2)', textDecoration: 'none' }}>
              Sign in
            </Link>
          </span>
        </div>
      </div>
    </div>
  );
}
